import { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useSchoolData } from '@/hooks/useSchoolData';
import { getId } from '@/utils/studentUtils';
import { ROUTES } from '@/utils/constants';
import { findLearners, NAVBAR_LEARNER_RESULT_LIMIT } from './learnerNavSearchUtils';

const SEARCH_ROLES = new Set(['teacher', 'admin', 'dev']);

export default function LearnerNavSearch({ currentRole }) {
  const navigate = useNavigate();
  const { students = [], isLoading } = useSchoolData();
  const containerRef = useRef(null);
  const compactInputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const results = useMemo(() => findLearners(students, query), [students, query]);
  const canSearch = SEARCH_ROLES.has(currentRole);

  useEffect(() => setActiveIndex(0), [query]);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [open]);

  if (!canSearch) return null;

  const viewProfile = (result) => {
    const studentId = getId(result.student);
    if (!studentId) return;
    setOpen(false);
    setQuery('');
    navigate(`${ROUTES.STUDENT_PROFILE}/${studentId}`);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (!results.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      viewProfile(results[activeIndex] || results[0]);
    }
  };

  const searchInput = ({ compact = false } = {}) => (
    <div className="relative">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-base-content/50" />
      <input
        ref={compact ? compactInputRef : undefined}
        type="search"
        value={query}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className={`input input-bordered w-full pl-9 ${compact ? '' : 'input-sm h-10 bg-base-200/70'}`}
        placeholder="Find learner…"
        aria-label="Find learner profile"
        aria-expanded={open}
        aria-controls="navbar-learner-results"
        aria-autocomplete="list"
        autoComplete="off"
      />
    </div>
  );

  return (
    <div ref={containerRef} className="relative md:w-64">
      <div className="hidden md:block">
        {searchInput()}
      </div>

      <button
        type="button"
        className="md:hidden p-2 rounded-lg transition-colors shadow-none hover:bg-base-200"
        onClick={() => {
          setOpen(true);
          window.requestAnimationFrame(() => compactInputRef.current?.focus());
        }}
        aria-label="Find learner profile"
        aria-expanded={open}
      >
        <MagnifyingGlassIcon className="h-5 w-5 hover:scale-110 transition-transform" />
      </button>

      {open && (
        <div className="fixed right-3 top-[5.5rem] z-[60] w-[min(18rem,calc(100vw-1.5rem))] rounded-xl border border-base-300 bg-base-100 p-2 shadow-xl md:absolute md:right-0 md:top-[calc(100%+0.5rem)] md:w-full">
          <div className="mb-2 md:hidden">
            {searchInput({ compact: true })}
          </div>

          <div className="mb-1 flex items-center justify-between px-2 py-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-base-content/50">Learners</p>
            {query.trim() && !isLoading && (
              <span className="text-[11px] text-base-content/50">
                {results.length === NAVBAR_LEARNER_RESULT_LIMIT ? `${NAVBAR_LEARNER_RESULT_LIMIT}+ matches` : `${results.length} ${results.length === 1 ? 'match' : 'matches'}`}
              </span>
            )}
          </div>

          <div id="navbar-learner-results" role="listbox" className="max-h-[min(28rem,calc(100vh-10rem))] overflow-y-auto">
            {!query.trim() ? (
              <p className="px-3 py-6 text-center text-sm text-base-content/55">Type a learner’s name to find their profile.</p>
            ) : isLoading ? (
              <p className="px-3 py-6 text-center text-sm text-base-content/55">Loading learners…</p>
            ) : results.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-base-content/55">No learners match “{query.trim()}”.</p>
            ) : (
              results.map((result, index) => {
                const studentId = getId(result.student);
                return (
                  <button
                    key={studentId || `${result.name}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => viewProfile(result)}
                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors ${
                      index === activeIndex ? 'bg-primary/10' : 'hover:bg-base-200'
                    }`}
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {result.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{result.name}</span>
                      <span className="block truncate text-xs text-base-content/55">{result.className || 'Learner'}</span>
                    </span>
                    <span className="shrink-0 text-[11px] font-medium text-primary">View profile</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
